// src/components/ClientDocumentsTab.js
//
// Phase 3D Frontend — Task 29
// Document upload UI for the Manage Clients → Documents tab.
//
// Handles:
//   - File picker (PDF / DOCX / TXT, 10MB max, client-side validation)
//   - Pre-creation of clientDocumentNarratives Firestore doc BEFORE Storage upload
//   - Upload to Cloud Storage via uploadBytesResumable
//   - Real-time document list via onSnapshot
//   - Status display: processing / ready / failed
//   - Expandable narrative preview (ready docs only)
//   - Pin toggle via toggleDocumentPin onCall
//   - Soft delete via deleteClientDocument onCall (with confirmation)
//   - Max 10 active / max 3 pinned enforcement in UI

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box, Typography, Button, Chip, CircularProgress, Alert,
  LinearProgress, IconButton, Tooltip, Collapse, Divider,
  Dialog, DialogTitle, DialogContent, DialogActions, Paper,
  List, ListItem, ListItemText, ListItemSecondaryAction
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  PushPin as PinIcon,
  PushPinOutlined as PinOutlinedIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Description as DocIcon,
  CheckCircle as ReadyIcon,
  HourglassEmpty as ProcessingIcon,
  ErrorOutline as FailedIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import {
  collection, doc, setDoc, onSnapshot,
  query, where, serverTimestamp
} from 'firebase/firestore';
import { ref, uploadBytesResumable } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { db, storage, functions, auth, appCheck } from '../firebase';
import { getToken } from 'firebase/app-check';
import { toast } from 'react-toastify';

// ─── Constants (must match documentIntelligenceService.js) ───────────────────
const MAX_ACTIVE_DOCS = 10;
const MAX_PINNED_DOCS = 3;
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB
const ALLOWED_MIME_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/plain',
];
const ALLOWED_EXTENSIONS_DISPLAY = 'PDF, DOCX, or TXT';

const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDate = (ts) => {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

// ─── Status chip helper ───────────────────────────────────────────────────────
const StatusChip = ({ status }) => {
  const map = {
    processing: { label: 'Processing', color: 'warning', icon: <ProcessingIcon fontSize="small" /> },
    ready:      { label: 'Ready',      color: 'success', icon: <ReadyIcon fontSize="small" /> },
    failed:     { label: 'Failed',     color: 'error',   icon: <FailedIcon fontSize="small" /> },
  };
  const cfg = map[status] || { label: status, color: 'default', icon: null };
  return (
    <Chip
      label={cfg.label}
      color={cfg.color}
      size="small"
      icon={cfg.icon}
      variant="outlined"
    />
  );
};

const ensureCallableAuthReady = async () => {
  const currentUser = auth.currentUser;

  if (!currentUser) {
    throw new Error('AUTH_NOT_READY');
  }

  await currentUser.getIdToken(true);
  return currentUser;
};

// ─── Main component ───────────────────────────────────────────────────────────
export default function ClientDocumentsTab({ clientId, user, clientIsActive }) {
  const [docs, setDocs] = useState([]);
  const [docsLoading, setDocsLoading] = useState(true);

  // Upload state
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);

  // UI state
  const [showDocsInfo, setShowDocsInfo] = useState(false);
  const [expandedDocId, setExpandedDocId] = useState(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [pinLoading, setPinLoading] = useState(null);   // docId while toggling
  const [deleteLoading, setDeleteLoading] = useState(null);

  // ─── Real-time document list ───────────────────────────────────────────────
  useEffect(() => {
    if (!clientId) return;
    setDocsLoading(true);

    const q = query(
      collection(db, 'clientDocumentNarratives'),
      where('clientId', '==', clientId),
      where('isDeleted', '==', false)
    );

    const unsub = onSnapshot(q, (snap) => {
      const fetched = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      // Sort: pinned first, then by uploadedAt desc
      fetched.sort((a, b) => {
        if (a.isPinned && !b.isPinned) return -1;
        if (!a.isPinned && b.isPinned) return 1;
        const aMs = a.uploadedAt?.toMillis?.() ?? 0;
        const bMs = b.uploadedAt?.toMillis?.() ?? 0;
        return bMs - aMs;
      });
      setDocs(fetched);
      setDocsLoading(false);
    }, (err) => {
      console.error('[ClientDocumentsTab] onSnapshot error:', err);
      setDocsLoading(false);
    });

    return () => unsub();
  }, [clientId]);

  // ─── Derived limits ────────────────────────────────────────────────────────
  const activeDocs = docs.filter(d => !d.isDeleted);
  const pinnedDocs = docs.filter(d => d.isPinned);
  const atMaxActive = activeDocs.length >= MAX_ACTIVE_DOCS;
  const atMaxPinned = pinnedDocs.length >= MAX_PINNED_DOCS;

  // ─── File validation ───────────────────────────────────────────────────────
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > MAX_FILE_SIZE) {
      toast.error(`File exceeds 10 MB limit. Please select a smaller file.`);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    if (!ALLOWED_MIME_TYPES.includes(file.type)) {
      toast.error(`Only ${ALLOWED_EXTENSIONS_DISPLAY} files are allowed.`);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    setSelectedFile(file);
  };

  // ─── Upload ────────────────────────────────────────────────────────────────
  const handleUpload = useCallback(async () => {
    if (!selectedFile || !clientId || !user?.uid) return;

    if (atMaxActive) {
      toast.error(`Maximum of ${MAX_ACTIVE_DOCS} documents reached. Delete a document before uploading.`);
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    // CRITICAL: Pre-create the Firestore document BEFORE uploading to Storage.
    // The Cloud Storage trigger (processClientDocumentUpload) reads metadata
    // from this doc. If the doc doesn't exist when the trigger fires, the
    // pipeline fails silently.
    let newDocRef;
    try {
      newDocRef = doc(collection(db, 'clientDocumentNarratives'));
      await setDoc(newDocRef, {
        extractionStatus: 'processing',
        isDeleted: false,
        lastProcessingEventId: null,
        clientId,
        fcfoId: user.uid,
        fileName: selectedFile.name,
        originalFileName: selectedFile.name,
        fileSize: selectedFile.size,
        mimeType: selectedFile.type,
        uploadedAt: serverTimestamp(),
        createdAt: serverTimestamp(),
        isPinned: false,
        narrative: null,
      });
    } catch (err) {
      console.error('[ClientDocumentsTab] Failed to pre-create Firestore doc:', err);
      toast.error('Failed to initialize upload. Please try again.');
      setUploading(false);
      return;
    }

     // Ensure App Check token is ready before upload (prevents 403 on enforced Storage)
    if (appCheck) {
      try {
        await getToken(appCheck);
      } catch (err) {
        console.warn('[ClientDocumentsTab] App Check token prefetch failed, proceeding anyway:', err.message);
      }
    }

    // Upload to Cloud Storage at the required path convention
    const storagePath = `clientDocuments/${clientId}/${newDocRef.id}/${selectedFile.name}`;
    const storageRefInstance = ref(storage, storagePath);
    const uploadTask = uploadBytesResumable(storageRefInstance, selectedFile);

    uploadTask.on(
      'state_changed',
      (snapshot) => {
        setUploadProgress((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
      },
      async (err) => {
        console.error('[ClientDocumentsTab] Upload error:', err);
        toast.error('Upload failed: ' + (err.code || err.message));
        // Clean up the pre-created Firestore doc on upload failure
        try {
          const { deleteDoc } = await import('firebase/firestore');
          await deleteDoc(newDocRef);
        } catch (cleanupErr) {
          console.error('[ClientDocumentsTab] Cleanup failed:', cleanupErr);
        }
        setUploading(false);
        setUploadProgress(0);
        if (fileInputRef.current) fileInputRef.current.value = '';
        setSelectedFile(null);
      },
      () => {
        // Upload complete — Cloud Function trigger takes over
        toast.info(`"${selectedFile.name}" uploaded. Processing in progress...`);
        setUploading(false);
        setUploadProgress(0);
        setSelectedFile(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
    );
  }, [selectedFile, clientId, user, atMaxActive]);

  // ─── Pin toggle ────────────────────────────────────────────────────────────
    const handleTogglePin = async (docItem) => {
    const isCurrentlyPinned = !!docItem.isPinned;

    if (!isCurrentlyPinned && atMaxPinned) {
      toast.warn(`Maximum of ${MAX_PINNED_DOCS} pinned documents reached. Unpin a document first.`);
      return;
    }

    setPinLoading(docItem.id);
    try {
      await ensureCallableAuthReady();

      const toggleDocumentPin = httpsCallable(functions, 'toggleDocumentPin');
      await toggleDocumentPin({ docId: docItem.id, clientId, pin: !isCurrentlyPinned });

      toast.success(isCurrentlyPinned ? 'Document unpinned.' : 'Document pinned.');
    } catch (err) {
      console.error('[ClientDocumentsTab] Pin toggle error:', err);

      if (err?.message === 'AUTH_NOT_READY' || err?.code === 'unauthenticated') {
        toast.error('Your session is still loading. Please wait a moment and try again.');
      } else {
        toast.error('Failed to update pin status. Please try again.');
      }
    } finally {
      setPinLoading(null);
    }
  };

  // ─── Delete ────────────────────────────────────────────────────────────────
      const handleDeleteConfirm = async () => {
    if (!deleteConfirmId) return;

    const targetDocId = deleteConfirmId;
    setDeleteLoading(targetDocId);
    setDeleteConfirmId(null);

    try {
      console.log("Auth state check:", {
        userProp: user,
        authCurrentUser: auth.currentUser,
        uid: auth.currentUser?.uid
      });

      await ensureCallableAuthReady();

      const deleteClientDocument = httpsCallable(functions, 'deleteClientDocument');
      await deleteClientDocument({ docId: targetDocId, clientId });

      toast.success('Document deleted.');
    } catch (err) {
      console.error('[ClientDocumentsTab] Delete error:', err);

      if (err?.message === 'AUTH_NOT_READY' || err?.code === 'unauthenticated') {
        toast.error('Your session is still loading. Please wait a moment and try again.');
      } else {
        toast.error('Failed to delete document. Please try again.');
      }
    } finally {
      setDeleteLoading(null);
    }
  };

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ p: 2 }}>

            {/* ── Documents Accordion ────────────────────────────────────────────── */}
      <Box display="flex" alignItems="center" gap={1} sx={{ mb: 1 }}>
        <Typography variant="subtitle1" fontWeight={600}>Documents</Typography>
        <IconButton size="small" onClick={() => setShowDocsInfo(prev => !prev)}>
          <InfoIcon fontSize="small" />
        </IconButton>
      </Box>
      <Collapse in={showDocsInfo}>
        <Alert severity="info" sx={{ mb: 2 }} onClose={() => setShowDocsInfo(false)}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>What documents are for:</strong> Upload documents to enrich your client’s AI context. After upload, the platform extracts a structured narrative that is automatically included in Q&amp;A answers and SavvaiSignals analysis.
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>Accepted formats:</strong> PDF, DOCX, or TXT. For financial statements, use the Financials tab — documents here are for non-financial context such as business plans, contracts, or strategic materials.
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>Document status:</strong> Ready = processed and active in AI context. Processing = extraction in progress. Failed = could not be processed.
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>Pinned documents</strong> are always included in AI context regardless of date. You can pin up to {MAX_PINNED_DOCS} documents.
          </Typography>
          <Typography variant="body2">
            <strong>Search</strong> finds documents by filename only — searching inside document contents is not supported. Maximum of {MAX_ACTIVE_DOCS} active documents per client.
          </Typography>
        </Alert>
      </Collapse>

      {/* ── Upload Section ─────────────────────────────────────────────────── */}
      <Paper variant="outlined" sx={{ p: 2.5, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          Upload Document
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Upload a document to enrich your client's AI context. Accepted formats: {ALLOWED_EXTENSIONS_DISPLAY}. Max size: 10 MB.
          After upload, the platform extracts a structured narrative that is automatically included in all Q&amp;A answers and SavvaiSignals analysis.
        </Typography>

        {atMaxActive && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Maximum of {MAX_ACTIVE_DOCS} documents reached. Delete a document before uploading a new one.
          </Alert>
        )}

        {!clientIsActive && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Activate this client to upload documents.
          </Alert>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            component="label"
            startIcon={<UploadIcon />}
            disabled={uploading || atMaxActive || !clientIsActive}
          >
            Choose File
            <input
              type="file"
              hidden
              ref={fileInputRef}
              accept=".pdf,.docx,.txt,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
              onChange={handleFileChange}
            />
          </Button>

          {selectedFile && (
            <Typography variant="body2" color="text.secondary">
              {selectedFile.name} ({formatFileSize(selectedFile.size)})
            </Typography>
          )}

          <Button
            variant="contained"
            onClick={handleUpload}
            disabled={!selectedFile || uploading || atMaxActive || !clientIsActive}
            startIcon={uploading ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {uploading ? 'Uploading...' : 'Upload'}
          </Button>
        </Box>

        {uploading && (
          <Box sx={{ mt: 2 }}>
            <LinearProgress variant="determinate" value={uploadProgress} />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              {uploadProgress < 100
                ? `${Math.round(uploadProgress)}% uploaded`
                : 'Finalizing...'}
            </Typography>
          </Box>
        )}
      </Paper>

      {/* ── Document List ──────────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Typography variant="subtitle1" fontWeight={600}>
          Uploaded Documents
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {activeDocs.length} / {MAX_ACTIVE_DOCS} active · {pinnedDocs.length} / {MAX_PINNED_DOCS} pinned
        </Typography>
      </Box>

      {docsLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      )}

      {!docsLoading && docs.length === 0 && (
        <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
          <DocIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
          <Typography color="text.secondary">
            No documents uploaded yet. Upload a document above to enrich this client's AI context.
          </Typography>
        </Paper>
      )}

      {!docsLoading && docs.length > 0 && (
        <List disablePadding>
          {docs.map((docItem, idx) => (
            <React.Fragment key={docItem.id}>
              {idx > 0 && <Divider />}
              <ListItem
                alignItems="flex-start"
                sx={{
                  bgcolor: docItem.isPinned ? 'primary.50' : 'transparent',
                  borderLeft: docItem.isPinned ? '3px solid' : '3px solid transparent',
                  borderColor: docItem.isPinned ? 'primary.main' : 'transparent',
                  pr: 16, // room for action buttons
                  py: 1.5,
                }}
              >
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography variant="body2" fontWeight={500}>
                        {docItem.fileName}
                      </Typography>
                      {docItem.isPinned && (
                        <Chip label="Pinned" size="small" color="primary" variant="filled" sx={{ height: 20 }} />
                      )}
                      <StatusChip status={docItem.extractionStatus} />
                    </Box>
                  }
                  secondary={
                    <Box sx={{ mt: 0.5 }}>
                      <Typography variant="caption" color="text.secondary">
                        Uploaded {formatDate(docItem.uploadedAt)} · {formatFileSize(docItem.fileSize || 0)}
                      </Typography>

                      {/* Narrative preview toggle — only when ready */}
                      {docItem.extractionStatus === 'ready' && docItem.narrative && (
                        <Box sx={{ mt: 1 }}>
                          <Button
                            size="small"
                            variant="text"
                            onClick={() => setExpandedDocId(
                              expandedDocId === docItem.id ? null : docItem.id
                            )}
                            endIcon={expandedDocId === docItem.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            sx={{ p: 0, textTransform: 'none', fontSize: '0.75rem' }}
                          >
                            {expandedDocId === docItem.id ? 'Hide summary' : 'View AI summary'}
                          </Button>
                          <Collapse in={expandedDocId === docItem.id} timeout="auto" unmountOnExit>
                            <Box
                              sx={{
                                mt: 1, p: 1.5,
                                bgcolor: 'grey.50',
                                borderRadius: 1,
                                border: '1px solid',
                                borderColor: 'grey.200',
                                maxHeight: 300,
                                overflowY: 'auto',
                              }}
                            >
                              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                                {docItem.narrative}
                              </Typography>
                            </Box>
                          </Collapse>
                        </Box>
                      )}

                      {docItem.extractionStatus === 'failed' && (
                        <Typography variant="caption" color="error.main" sx={{ display: 'block', mt: 0.5 }}>
                          Processing failed. Delete this document and try uploading again.
                        </Typography>
                      )}
                    </Box>
                  }
                />

                {/* Action buttons */}
                <ListItemSecondaryAction sx={{ top: '50%', transform: 'translateY(-50%)' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {/* Pin toggle */}
                    <Tooltip
                      title={
                        docItem.isPinned
                          ? 'Unpin document'
                          : atMaxPinned
                          ? `Max ${MAX_PINNED_DOCS} pinned documents reached`
                          : 'Pin document (included in all AI context)'
                      }
                    >
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => handleTogglePin(docItem)}
                          disabled={
                            pinLoading === docItem.id ||
                            deleteLoading === docItem.id ||
                            (!docItem.isPinned && atMaxPinned)
                          }
                          color={docItem.isPinned ? 'primary' : 'default'}
                        >
                          {pinLoading === docItem.id
                            ? <CircularProgress size={16} />
                            : docItem.isPinned
                            ? <PinIcon fontSize="small" />
                            : <PinOutlinedIcon fontSize="small" />
                          }
                        </IconButton>
                      </span>
                    </Tooltip>

                    {/* Delete */}
                    <Tooltip title="Delete document">
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => setDeleteConfirmId(docItem.id)}
                          disabled={deleteLoading === docItem.id || pinLoading === docItem.id}
                          color="error"
                        >
                          {deleteLoading === docItem.id
                            ? <CircularProgress size={16} />
                            : <DeleteIcon fontSize="small" />
                          }
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                </ListItemSecondaryAction>
              </ListItem>
            </React.Fragment>
          ))}
        </List>
      )}

      {/* ── Delete Confirmation Dialog ─────────────────────────────────────── */}
      <Dialog
        open={!!deleteConfirmId}
        onClose={() => setDeleteConfirmId(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Document?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This document and its AI-synthesized narrative will be permanently removed from this client's context.
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmId(null)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
}